import React, { useContext, useState } from 'react'
import { GridColumn as Column, Grid, GridSortChangeEvent } from '@progress/kendo-react-grid'
import { AppropriatenessTestDto } from 'api/Clients/Types/AppropriatenessTestDto'
import ClientAreaAuditLogModal from 'components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import { FilterCell } from 'components/Filter/FilterCell'
import { Loading } from 'components/Loading'
import MoreButton from 'components/MoreButton/MoreButton'
import { errorToast, ToastContext } from 'components/Toast/Toast'
import { GridState } from 'features/DataGrid/hooks/useGridState'
import { GridStateStatusState } from 'features/DataGrid/hooks/useGridStatusState'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'
import TableWithExport from 'utils/table/withExport'

import { Pager } from '../../../components/components/Pager'
import { TableStateContext, TableStateContextData } from '../../../utils/TableStateContext'
import { appropriatenessTestColumns } from './columns'
import { AppropriatnessTestScoreSetting } from './interface'
import ScoreSettingsTable from './ScoreSettingsTable'

interface TestsTableProps {
  tests: AppropriatenessTestDto[]
  gridState: GridState
  gridStatusState: GridStateStatusState
  tableData: TableStateContextData
  setTableData: React.Dispatch<React.SetStateAction<TableStateContextData>>
  handleSortChange(event: GridSortChangeEvent): Promise<void>
  handleSkipCountChange(skipCount: boolean): Promise<void>
  handleGridPageChange(page: number): Promise<void>
  handlePageSizeChange(pageSize: number): Promise<void>
  hideScoreSettings?: boolean
}

const TestsTable: React.FC<TestsTableProps> = ({
  tests,
  gridStatusState,
  tableData,
  setTableData,
  gridState,
  handleSortChange,
  handleSkipCountChange,
  handleGridPageChange,
  handlePageSizeChange,
  hideScoreSettings = false,
}) => {
  const [showScoreSettings, setShowScoreSettings] = useState(false)
  const [selectedTest, setSelectedTest] = useState<AppropriatenessTestDto | null>(null)
  const [auditLogVisible, setAuditLogVisible] = useState(false)
  const clientsApiClient = useApiClient(ClientsApiClient)
  const setToast = useContext(ToastContext)

  const handleOpenScoreSettings = (row: AppropriatenessTestDto) => {
    if (row && row.category === 'Appropriateness Test') {
      setSelectedTest(row)
      setShowScoreSettings(true)
    }
  }

  const handleCloseScoreSettings = () => {
    setShowScoreSettings(false)
    setSelectedTest(null)
  }

  const handleSaveScoreSettings = async (data: { pass: string; fail: string }) => {
    if (!selectedTest?.id) {
      setShowScoreSettings(false)
      return
    }
    const body: AppropriatnessTestScoreSetting = {
      preferredScore: Number(data.pass),
      failScore: Number(data.fail),
    }
    try {
      await clientsApiClient.updateAppropriatenessTestScoreSettings(selectedTest.id, body)
    } catch (error) {
      setToast(errorToast('Failed to send email.' + error))
    } finally {
      setShowScoreSettings(false)
      setSelectedTest(null)
    }
  }

  return (
    <>
      {showScoreSettings ? (
        <ScoreSettingsTable
          visible={true}
          onClose={handleCloseScoreSettings}
          onSave={handleSaveScoreSettings}
          testData={tests}
        />
      ) : (
        <Loading isLoading={gridStatusState.isLoading}>
          {tests && (
            <TableStateContext.Provider value={[tableData, setTableData]}>
              <TableWithExport data={tests} columns={appropriatenessTestColumns}>
                <Grid
                  {...gridState}
                  resizable
                  data={tests}
                  sort={gridState.sort}
                  total={gridState.total}
                  take={gridState.take}
                  skip={gridState.skip}
                  onSortChange={handleSortChange}
                  pageable={true}
                  pager={() => (
                    <Pager
                      onSkipCountChange={handleSkipCountChange}
                      onPageChange={handleGridPageChange}
                      onPageSizeChange={handlePageSizeChange}
                      page={gridState.pageIndex}
                      pageSize={gridState.pageSize}
                      take={gridState.take || 10}
                      skip={gridState.skip || 0}
                    />
                  )}
                  style={{ zIndex: 0 }}
                >
                  {appropriatenessTestColumns
                    .filter((c) => c.show !== false)
                    .map((column, idx) => {
                      if (column.title === 'Score settings') {
                        if (hideScoreSettings) {
                          return null
                        }
                        return (
                          <Column
                            key={idx + 2}
                            {...column}
                            filterCell={FilterCell}
                            cell={(props) => (
                              <td>
                                {column.field ? props.dataItem[column.field] : null}
                                <MoreButton
                                  items={[
                                    {
                                      name: 'Score Settings',
                                      'data-test': 'score-settings',
                                      onClick: () => handleOpenScoreSettings(props.dataItem),
                                      disabled: props.dataItem.category !== 'Appropriateness Test',
                                    },
                                    {
                                      name: 'Audit Log',
                                      'data-test': 'audit-log',
                                      onClick: () => {
                                        setSelectedTest(props.dataItem)
                                        setAuditLogVisible(true)
                                      },
                                    },
                                    {
                                      name: 'Duplicate',
                                      'data-test': 'duplicate',
                                      onClick: () => alert('Backend not ready.'),
                                    },
                                  ]}
                                  data-test='more-button'
                                />
                              </td>
                            )}
                          />
                        )
                      }
                      return <Column key={idx + 2} {...column} />
                    })}
                </Grid>
              </TableWithExport>
            </TableStateContext.Provider>
          )}
        </Loading>
      )}
      {auditLogVisible && selectedTest && (
        <ClientAreaAuditLogModal
          isVisible={auditLogVisible}
          onClose={() => setAuditLogVisible(false)}
          testbuilderId={selectedTest.id}
        />
      )}
    </>
  )
}

export default TestsTable
